import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { getPublishedProjects } from "../lib/api";
import { useSite, githubHandle } from "../lib/site";
import { EditableText } from "../components/EditableText";
import { Seo } from "../components/Seo";

export default function About() {
  const [published, setPublished] = useState(null);
  const { site } = useSite();

  useEffect(() => {
    getPublishedProjects()
      .then(setPublished)
      .catch(() => setPublished([]));
  }, []);

  return (
    <div data-testid="about-page">
      <Seo
        title="About Me — oVu Cyber"
        description="Honest background of the high school student behind oVu Cyber: interests in Linux, networking, web security, Python and CTFs, plus current learning goals."
      />
      {/* Intro */}
      <section className="py-20 md:py-24 px-6 bg-ink border-b border-white/10">
        <div className="max-w-6xl mx-auto">
          <span className="font-mono text-xs uppercase tracking-widest text-cyber block mb-2"><EditableText k="about_label" defaultValue="// STUDENT DOSSIER" /></span>
          <h1 className="font-display text-3xl sm:text-5xl text-white font-bold uppercase mb-6">
            <EditableText k="about_title_1" defaultValue="Curious, Focused &" /> <span className="text-cyber"><EditableText k="about_title_2" defaultValue="Verifiable" /></span>
          </h1>
          <p className="text-dim text-base md:text-lg max-w-2xl leading-relaxed mb-10">
            {site.bio}
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 font-mono text-xs text-[#cbd5e1] max-w-3xl">
            <div className="p-4 rounded bg-panel border border-white/10">
              <span className="text-dim block mb-1">
                <EditableText k="about_toolset_label" defaultValue="Primary Toolset" />
              </span>
              <span className="text-cyber">{site.toolset}</span>
            </div>
            <div className="p-4 rounded bg-panel border border-white/10">
              <span className="text-dim block mb-1">
                <EditableText k="about_github_label" defaultValue="GitHub Identity" />
              </span>
              <a href={site.github_url} target="_blank" rel="noopener noreferrer" className="text-cyber hover:underline" data-testid="about-github-link">github.com/{githubHandle(site.github_url)}</a>
            </div>
            <div className="p-4 rounded bg-panel border border-white/10">
              <span className="text-dim block mb-1">
                <EditableText k="about_goal_label" defaultValue="Goal" />
              </span>
              <span className="text-cyber">{site.goal_text}</span>
            </div>
          </div>
        </div>
      </section>

      {/* Studying vs demonstrated — the honest split */}
      <section className="py-20 px-6 bg-deep border-b border-white/10">
        <div className="max-w-6xl mx-auto">
          <span className="font-mono text-xs uppercase tracking-widest text-cyber block mb-2"><EditableText k="about_ledger_label" defaultValue="// HONEST LEDGER" /></span>
          <h2 className="font-display text-2xl sm:text-3xl text-white font-bold uppercase mb-10"><EditableText k="about_ledger_title" defaultValue="Studying vs. Demonstrated" /></h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="p-6 rounded-xl bg-ink border border-white/10" data-testid="about-studying-panel">
              <h3 className="font-mono text-xs uppercase tracking-widest text-cyber mb-4"><EditableText k="about_studying_head" defaultValue="$ currently_studying" /></h3>
              <div className="flex flex-wrap gap-2 mb-6">
                {site.studying.map((t) => (
                  <span key={t} className="font-mono text-xs px-3 py-1.5 rounded bg-cyber/10 text-cyber border border-cyber/30">{t}</span>
                ))}
              </div>
              <h4 className="font-mono text-xs uppercase tracking-widest text-amber-400 mb-3"><EditableText k="about_horizon_head" defaultValue="$ on_the_horizon" /></h4>
              <div className="flex flex-wrap gap-2">
                {site.horizon.map((t) => (
                  <span key={t} className="font-mono text-xs px-3 py-1.5 rounded bg-amber-500/10 text-amber-400 border border-amber-500/30">{t}</span>
                ))}
              </div>
              <p className="text-xs text-dim mt-6 leading-relaxed">
                <EditableText k="about_studying_note" multiline defaultValue="These are topics I am actively learning — not claims of mastery." />
              </p>
            </div>
            <div className="p-6 rounded-xl bg-ink border border-white/10" data-testid="about-demonstrated-panel">
              <h3 className="font-mono text-xs uppercase tracking-widest text-emerald-400 mb-4"><EditableText k="about_demonstrated_head" defaultValue="$ demonstrated_through_projects" /></h3>
              {published === null ? (
                <p className="font-mono text-xs text-dim">
                  <EditableText k="about_demonstrated_loading" defaultValue="loading ..." />
                </p>
              ) : published.length === 0 ? (
                <p className="text-xs text-dim leading-relaxed" data-testid="about-demonstrated-empty">
                  <EditableText k="about_demonstrated_empty" multiline defaultValue="Nothing published yet. My first projects are in progress — when they are finished and public, the skills they prove will be listed here automatically. Until then, this section stays honest and empty." />
                </p>
              ) : (
                <ul className="space-y-3" data-testid="about-demonstrated-list">
                  {published.map((p) => (
                    <li key={p.id} className="flex items-start justify-between gap-4 font-mono text-xs border-b border-white/5 pb-3">
                      <span className="text-white">{p.title}</span>
                      <span className="text-cyber text-right">{(p.tech || []).join(" • ")}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Goals */}
      <section className="py-20 px-6 bg-panelalt border-b border-white/10">
        <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <span className="font-mono text-xs uppercase tracking-widest text-cyber block mb-2"><EditableText k="about_focus_label" defaultValue="// CURRENT FOCUS" /></span>
            <h2 className="font-display text-2xl sm:text-3xl text-white font-bold uppercase mb-8"><EditableText k="about_goals_title" defaultValue="Learning Goals" /></h2>
            <ol className="space-y-4">
              {site.goals.map((goal, i) => (
                <li key={goal} className="flex items-start gap-4 p-4 rounded bg-ink border border-white/10" data-testid={`about-goal-${i}`}>
                  <span className="font-mono text-cyber text-xs font-bold mt-0.5">{String(i + 1).padStart(2, "0")}</span>
                  <span className="text-sm text-[#cbd5e1]">{goal}</span>
                </li>
              ))}
            </ol>
            <Link to="/projects" className="inline-block mt-8 font-mono text-xs text-cyber hover:underline uppercase tracking-wider" data-testid="about-projects-link">
              [ <EditableText k="about_projects_link" defaultValue="See what I have shipped →" /> ]
            </Link>
          </div>
          <div className="lg:col-span-5">
            <figure className="relative aspect-[4/3] rounded-xl border border-white/10 overflow-hidden bg-panel">
              <img
                src="https://images.unsplash.com/photo-1608742213509-815b97c30b36?crop=entropy&cs=srgb&fm=jpg&q=85"
                alt="Linux terminal with audit logs in a dark theme"
                className="w-full h-full object-cover opacity-80"
                loading="lazy"
                data-testid="about-lab-image"
              />
              <figcaption className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-deep/90 to-transparent font-mono text-xs text-dim">
                <EditableText k="about_image_caption" defaultValue="Terminal time: where most of the learning happens" />
              </figcaption>
            </figure>
          </div>
        </div>
      </section>
    </div>
  );
}
